import * as signalR from '@microsoft/signalr';

// Hub URL: use runtime config (config.js) if available, fallback to same-origin
const HUB_URL = window.CHATBOT_CONFIG?.HUB_URL
  || `http://${window.location.hostname}:5500/hubs/chat`;

class SignalRService {
  constructor() {
    this.connection = null;
    this.callbacks = {
      onMessage: [],
      onMessageSent: [],
      onUserOnline: [],
      onUserOffline: [],
      onTyping: [],
      onMessageRead: [],
      onConnected: [],
      onDisconnected: [],
      onPatientQueueUpdated: [],
      onPatientMessage: [],
      onPatientSessionAssigned: [],
    };
  }

  async connect(token) {
    if (this.connection) {
      await this.disconnect();
    }

    // Don't reset callbacks - they are registered before connect() is called

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL, {
        accessTokenFactory: () => token
        // Allow negotiation for better compatibility with mobile browsers
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // Register event handlers
    this.connection.on('ReceiveMessage', (message) => {
      this.callbacks.onMessage.forEach(cb => cb(message));
    });

    this.connection.on('MessageSent', (message) => {
      this.callbacks.onMessageSent.forEach(cb => cb(message));
    });

    this.connection.on('UserOnline', (userId) => {
      this.callbacks.onUserOnline.forEach(cb => cb(userId));
    });

    this.connection.on('UserOffline', (userId) => {
      this.callbacks.onUserOffline.forEach(cb => cb(userId));
    });

    this.connection.on('UserTyping', (userId, userName, departmentId) => {
      this.callbacks.onTyping.forEach(cb => cb(userId, userName, departmentId));
    });

    this.connection.on('MessageRead', (messageId, readerId) => {
      this.callbacks.onMessageRead.forEach(cb => cb(messageId, readerId));
    });

    this.connection.on('PatientQueueUpdated', () => {
      this.callbacks.onPatientQueueUpdated.forEach(cb => cb());
    });

    this.connection.on('PatientMessage', (msg) => {
      this.callbacks.onPatientMessage.forEach(cb => cb(msg));
    });

    this.connection.on('PatientSessionAssigned', (data) => {
      this.callbacks.onPatientSessionAssigned.forEach(cb => cb(data));
    });

    this.connection.onreconnecting(() => {
      console.log('SignalR: Reconnecting...');
    });

    this.connection.onreconnected(() => {
      console.log('SignalR: Reconnected');
      this.callbacks.onConnected.forEach(cb => cb());
    });

    this.connection.onclose(() => {
      console.log('SignalR: Disconnected');
      this.callbacks.onDisconnected.forEach(cb => cb());
    });

    try {
      await this.connection.start();
      console.log('SignalR: Connected');
      this.callbacks.onConnected.forEach(cb => cb());
      return true;
    } catch (err) {
      console.error('SignalR connection error:', err);
      return false;
    }
  }

  async disconnect() {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
    }
  }

  isConnected() {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }

  // Send message via SignalR
  async sendMessage(message) {
    if (!this.isConnected()) return;
    await this.connection.invoke('SendMessage', message);
  }

  // Join department room
  async joinDepartment(departmentId) {
    if (!this.isConnected()) return;
    await this.connection.invoke('JoinDepartment', departmentId);
  }

  // Leave department room
  async leaveDepartment(departmentId) {
    if (!this.isConnected()) return;
    await this.connection.invoke('LeaveDepartment', departmentId);
  }

  // Mark message as read
  async markAsRead(messageId, senderId) {
    if (!this.isConnected()) return;
    await this.connection.invoke('MarkAsRead', messageId, senderId);
  }

  // Send typing indicator
  async sendTyping(receiverId, departmentId) {
    if (!this.isConnected()) return;
    await this.connection.invoke('Typing', receiverId, departmentId);
  }

  // Event subscriptions
  onMessage(callback) {
    this.callbacks.onMessage.push(callback);
    return () => {
      this.callbacks.onMessage = this.callbacks.onMessage.filter(cb => cb !== callback);
    };
  }

  onMessageSent(callback) {
    this.callbacks.onMessageSent.push(callback);
    return () => {
      this.callbacks.onMessageSent = this.callbacks.onMessageSent.filter(cb => cb !== callback);
    };
  }

  onUserOnline(callback) {
    this.callbacks.onUserOnline.push(callback);
    return () => {
      this.callbacks.onUserOnline = this.callbacks.onUserOnline.filter(cb => cb !== callback);
    };
  }

  onUserOffline(callback) {
    this.callbacks.onUserOffline.push(callback);
    return () => {
      this.callbacks.onUserOffline = this.callbacks.onUserOffline.filter(cb => cb !== callback);
    };
  }

  onTyping(callback) {
    this.callbacks.onTyping.push(callback);
    return () => {
      this.callbacks.onTyping = this.callbacks.onTyping.filter(cb => cb !== callback);
    };
  }

  onMessageRead(callback) {
    this.callbacks.onMessageRead.push(callback);
    return () => {
      this.callbacks.onMessageRead = this.callbacks.onMessageRead.filter(cb => cb !== callback);
    };
  }

  onConnected(callback) {
    this.callbacks.onConnected.push(callback);
    return () => {
      this.callbacks.onConnected = this.callbacks.onConnected.filter(cb => cb !== callback);
    };
  }

  onDisconnected(callback) {
    this.callbacks.onDisconnected.push(callback);
    return () => {
      this.callbacks.onDisconnected = this.callbacks.onDisconnected.filter(cb => cb !== callback);
    };
  }

  onPatientQueueUpdated(callback) {
    this.callbacks.onPatientQueueUpdated.push(callback);
    return () => {
      this.callbacks.onPatientQueueUpdated = this.callbacks.onPatientQueueUpdated.filter(cb => cb !== callback);
    };
  }

  onPatientMessage(callback) {
    this.callbacks.onPatientMessage.push(callback);
    return () => {
      this.callbacks.onPatientMessage = this.callbacks.onPatientMessage.filter(cb => cb !== callback);
    };
  }

  onPatientSessionAssigned(callback) {
    this.callbacks.onPatientSessionAssigned.push(callback);
    return () => {
      this.callbacks.onPatientSessionAssigned = this.callbacks.onPatientSessionAssigned.filter(cb => cb !== callback);
    };
  }
}

export const signalRService = new SignalRService();
export default signalRService;
